import * as React from 'react'
import {Suspense, useEffect, useState} from 'react'
import {Helmet} from 'react-helmet'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import CarouselOutline from '../components/carouselOutline'
import '../css/marketResearch.css'
import {ACCENT_TEXT, goToElementId, setTitleFont, tagger} from '../util/util'

const MarketResearchAnimation = React.lazy(() => import('../lotties/market_research/marketResearch'))
const DeviceAnimation = React.lazy(() => import('../lotties/device/device'))
const PhoneAnimation = React.lazy(() => import('../lotties/phone/phone'))
const PersonAnimation = React.lazy(() => import('../lotties/person/person'))
const WorldMap = React.lazy(() => import('../svg/worldMap'))

const continentToCounterMap = new Map([
    ['Africa', '360,000'],
    ['North America', '4,500,000'],
    ['Europe', '1,900,000'],
    ['Asia', '1,300,000'],
    ['Australia', '200,000'],
    ['South America', '600,000']
])

const MarketResearch = () => {

    const {t, i18n} = useTranslation('market-research')
    const tC = useTranslation('common').t
    const tL = useTranslation('landing').t

    const [state, setState] = useState({
        continent: undefined as any
    })

    useEffect(() => {
        setTitleFont(i18n.language)
    }, [])

    const scrollToHandler = () => goToElementId('reachGlobal', true)

    const audienceListBlocks = [
        {
            title: t('Surveytime'),
            text: t('Surveytime is a global'),
            img: 'https://static.persona.ly/images/market_research_page/group-15.svg',
            audience: {
                demo: '18+',
                geoList: ['USA']
            }
        },
        {
            title: t('Offer Wall'),
            text: t('Offer Wall is integrated'),
            img: 'https://static.persona.ly/images/market_research_page/group-33.svg',
            audience: {
                demo: '18-35',
                geoList: ['USA', 'IN', 'RU', 'BR']
            }
        },
        {
            title: t('Gifthulk'),
            text: t('Founded in 2011'),
            img: 'https://static.persona.ly/images/market_research_page/group-34.svg',
            audience: {
                demo: '35+',
                geoList: ['USA']
            }
        }
    ]

    return (
        <div className="market-research">
            <div className="down-scroll not-tablet not-mobile" onClick={scrollToHandler}>
                <p className="down-scroll-text">SCROLL</p>
                <span className="down-scroll-animation down-scroll-animation-blue"> </span>
            </div>
            <Helmet>
                <title>Market Research - Gain consumer insights for your products or brand</title>
                <meta name="title" content="Market Research - Gain consumer insights for your products or brand"/>
                <meta name="description" content="Persona.ly provides market research sampling solutions, delivering quality data at scale from diverse audiences"/>
                <meta name="keywords" content="Market Research, Sample Providers, Market Research Panel, Consumer Insights, Surveytime, Gifthulk, Offer Wall, Survey Panel"/>
            </Helmet>
            <div className="main-content">
                <div>
                    <div className="title muli not-desktop not-mobile">{tagger(t('Data-Driven'), ACCENT_TEXT)}</div>
                    <div className="market-research-c">
                        <div className={'tablet market-research-mr'}>
                            <div className="title muli not-tablet">{tagger(t('Data-Driven'), ACCENT_TEXT)}</div>
                            <div className="block-p">{t('Quickly gain global')}</div>
                            <div className="block-c">{tagger(t('Personaly provides'), ACCENT_TEXT)}</div>
                            <div className="two-buttons">
                                <Link to="mrcontact">
                                    <button className="button-blue large">{tC('Start Now')}<img src="https://static.persona.ly/images/arrow.svg"/></button>
                                </Link>
                                <Link to="about#contact">
                                    <button className="button-grey large">{tC('Contact Us')}</button>
                                </Link>
                            </div>
                            <div
                                className="block-c trusted not-tablet not-mobile trusted-by">{tL('Trusted by')}</div>
                            <img className="not-tablet not-mobile" src="https://static.persona.ly/images/partners/market-research-trusted.svg"/>
                        </div>
                        <div className="lottie-market-research-container tablet">
                            <Suspense fallback={<div/>}>
                                <MarketResearchAnimation/>
                            </Suspense>
                        </div>
                    </div>
                    <div className="block-c supported not-desktop trusted-by">{tL('Trusted by')}</div>
                    <img className="not-desktop" src="https://static.persona.ly/images/partners/market-research-trusted.svg"/>
                </div>
                <div id="reachGlobal" className="full-block">
                    <div className="mid-block">
                        <div className="blog muli">{t('Reach Global')}</div>
                        <div className="block-c">{tagger(t('Our multiple panels'), 'accent-text fw700')}</div>
                    </div>
                </div>
                <div style={{margin: '2.5% 0 1.5% 0'}}>
                    <CarouselOutline blocks={audienceListBlocks}/>
                </div>
                {/* TODO: rework */}
                <div style={{marginTop: 0}}></div>
                <div className="full-block">
                    <div className="mid-block">
                        <div className="blog muli">{t('Global Reach At Scale')}</div>
                        <div className="block-c">{tagger(t('Our panels reach'), ACCENT_TEXT)}</div>
                    </div>
                </div>
                <div className="registered-map" onMouseLeave={() => setState({continent: undefined})}>
                    <Suspense fallback={<div/>}>
                        <WorldMap callback={e => setState({continent: e.currentTarget.id})}/>
                    </Suspense>
                </div>
                <div className="registered-users">
                    <div className="registered-users-count">
                        {state.continent ? continentToCounterMap.get(state.continent) : '9,000,000'}
                    </div>
                    <div className="registered-users-title">
                        {state.continent ? 'active panelists in ' + state.continent : t('Registered Users Worldwide')}
                    </div>
                </div>
                <div className="full-block where-technology">
                    <div className="mid-block">
                        <div className="blog muli">{t('Where Technology Meets')}</div>
                        <div
                            className="block-c">{tagger(t('In less than 100 miliseconds'), 'accent-text fw700')}</div>
                    </div>
                </div>
                <div className="part-after-title pat-1">
                    <div className="block-left">
                        <div>
                            <div className="block-p not-mobile">{t('Machine Learning')}</div>
                            <div className="block-c not-mobile">{t('our algorithms title')}</div>
                        </div>
                        <div className="lottie-device-container">
                            <Suspense fallback={<div/>}>
                                <DeviceAnimation/>
                            </Suspense>
                        </div>
                        <div className="block-p not-tablet not-desktop">{t('Machine Learning')}</div>
                        <div className="block-c not-tablet not-desktop">{t('our algorithms title')}</div>
                    </div>
                    <div className="block-right">
                        <div className="block-p">{t('Additional Features')}</div>
                        <div className="additional-features">
                            <div className="additional-features-container">
                                <div className="additional-feature-container">
                                    <div style={{minHeight: 100}}>
                                        <img className="additional-features-icon" src="https://static.persona.ly/images/group-16.svg"/>
                                    </div>
                                    <div className="additional-features-text-container">
                                        <div className="additional-features-title">{t('API Integration')}</div>
                                        <div className="additional-features-text">{t('Manage your survey')}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="additional-features-container">
                                <div className="additional-feature-container">
                                    <div style={{minHeight: 100}}>
                                        <img className="additional-features-icon" src="https://static.persona.ly/images/02.svg"/>
                                    </div>
                                    <div className="additional-features-text-container">
                                        <div className="additional-features-title">{t('Feasibility & Cost')}</div>
                                        <div className="additional-features-text">{t('Quick estimation of')}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="additional-features-container">
                                <div className="additional-feature-container">
                                    <div style={{minHeight: 100}}>
                                        <img className="additional-features-icon" src="https://static.persona.ly/images/03.svg"/>
                                    </div>
                                    <div className="additional-features-text-container">
                                        <div
                                            className="additional-features-title">{t('Multi-Language Support')}</div>
                                        <div className="additional-features-text">{t('Our algorithms match')}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="full-block">
                    <div className="mid-block">
                        <div className="blog muli">{t('We Ensure Data Quality')}</div>
                        <div className="block-c">{t('By monitoring over')}</div>
                    </div>
                </div>
                <div className="part-after-title pat-2">
                    <div className="">
                        <div className="quality-container">
                            <div className="lottie-phone-container">
                                <Suspense fallback={<div/>}>
                                    <PhoneAnimation/>
                                </Suspense>
                            </div>
                            <div className="quality-text">
                                <div className="quality-title">{t('Device Recognition')}</div>
                                <div className="quality-section">{t('Email')} + {t('Mobile Verification')}</div>
                                <div className="quality-section">{t('Device Fingerprinting')}</div>
                                <div className="quality-section">{t('Device Reputation')}</div>
                                <div
                                    className="quality-section">{t('Physical Address')} + {t('Proxy Detection')}</div>
                            </div>
                        </div>
                    </div>
                    <div className="">
                        <div className="quality-container">
                            <div className="lottie-person-container">
                                <Suspense fallback={<div/>}>
                                    <PersonAnimation/>
                                </Suspense>
                            </div>
                            <div className="quality-text">
                                <div className="quality-title">{t('Human Recognition')}</div>
                                <div className="quality-section">{t('Red Herring Question')}</div>
                                <div className="quality-text-details">{t('An effective measure for')}</div>

                                <div className="quality-section">{t('ReCaptcha')}</div>
                                <div className="quality-text-details">{t('Real-time detection')}</div>

                                <div className="quality-section">{t('Pattern Analysis')}</div>
                                <div className="quality-text-details">{t('Automatic detection')}</div>

                                <div className="quality-section">{t('LOI Comparisons')}</div>
                                <div className="quality-text-details">{t('Monitors survey')}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default MarketResearch



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/pages/marketResearch.tsx